import { Schedule } from "@/models/Schedule"

class Lesson {
  constructor (
    id,
    teacherId,
    parentId,
    studentId,
    schedule,
    wage,
    note
  ){
    if (!(typeof id === 'string')) {
      throw new TypeError(`The type of 'id' must be String. Actual: ${typeof id}`)
    }
    if (!(typeof teacherId === 'string')) {
      throw new TypeError(`The type of 'teacherId' must be String. Actual: ${typeof teacherId}`)
    }
    if (!(typeof parentId === 'string')) {
      throw new TypeError(`The type of 'parentId' must be String. Actual: ${typeof parentId}`)
    }
    if (!(typeof studentId === 'string')) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    if (!(schedule instanceof Schedule)) {
      throw new TypeError(`'schedule' must be an instance of [object Schedule]. Actual: ${typeof schedule}`)
    }
    if (!(typeof wage === 'number')) {
      throw new TypeError(`The type of 'wage' must be Number. Actual: ${typeof wage}`)
    }
    if (!(typeof note === 'string')) {
      throw new TypeError(`The type of 'note' must be String. Actual: ${typeof note}`)
    }
    this.id = id
    this.teacherId = teacherId,
    this.parentId = parentId,
    this.studentId = studentId,
    this.schedule = schedule,
    this.wage = wage,
    this.note = note
  }

  static createFromObject(object) {
    if (!(object instanceof Object)) {
      throw new TypeError(`The type of data must be [object Object]. Actual: ${typeof object}`)
    }
    return new Lesson(
      object.id,
      object.data.teacherId,
      object.data.parentId,
      object.data.studentId,
      Schedule.createFromObject(object.data.schedule),
      object.data.wage,
      object.data.note
    )
  }

  hasId(id) {
    if (!(typeof id === "string")) {
      throw new TypeError(`The type of 'id' must be String. Actual: ${typeof id}`)
    }
    return (this.id === id)
  }

  hasStudentId(studentId) {
    if (!(typeof studentId === 'string')) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    return (this.studentId === studentId)
  }

  hasTeacherId(teacherId) {
    if (!(typeof teacherId === 'string')) {
      throw new TypeError(`The type of 'teacherId' must be String. Actual: ${typeof teacherId}`)
    }
    return (this.teacherId === teacherId)
  }

  is(other) {
    if(!(other instanceof Lesson)) {
      throw new TypeError(`'other' must be an instance of [object Lesson]. Actual: ${other}`)
    }
    return (this.id === other.id & this.teacherId === other.teacherId & this.parentId === other.parentId & this.studentId === other.studentId & this.schedule.is(other.schedule) & this.wage === other.wage & this.note === other.note)
  }

  toObject () {
    return {
      id: this.id,
      data: {
        teacherId: this.teacherId,
        parentId: this.parentId,
        studentId: this.studentId,
        schedule: this.schedule.toObject(),
        wage: this.wage,
        note: this.note
      }
    }
  }

  updateSchedule(schedule) {
    if (!(schedule instanceof Schedule)) {
      throw new TypeError(`'schedule' must be an instance of [object Schedule]. Actual: ${typeof schedule}`)
    }
    this.schedule = schedule
    return this
  }

  updateWage(wage) {
    if (!(typeof wage === 'number')) {
      throw new TypeError(`The type of 'wage' must be Number. Actual: ${typeof wage}`)
    }
    this.wage = wage
    return this
  }
}

class Lessons {
  constructor(lessons) {
    if (!(lessons instanceof Array)) {
      throw new TypeError(`'lessons' must be an instance of [object Array]. Actual: ${typeof lessons}`)
    }
    this.lessons = lessons
  }

  static createFromObjects(objects) {
    if (!(objects instanceof Array)) {
      throw new TypeError(`'objects' must be an instance of Array. Actual: ${typeof objects}`)
    }
    const arr = objects.map(obj => {
      return Lesson.createFromObject(obj)
    })
    return new Lessons(arr)
  }

  filterByStudentId(studentId) {
    if (!(typeof studentId === 'string')) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    return new Lessons(this.lessons.filter(lesson => lesson.hasStudentId(studentId)))
  }

  filterByTeacherId(teacherId) {
    if (!(typeof teacherId === 'string')) {
      throw new TypeError(`The type of 'teacherId' must be String. Actual: ${typeof teacherId}`)
    }
    return new Lessons(this.lessons.filter(lesson => lesson.hasTeacherId(teacherId)))
  }

  find(id) {
    if (!(typeof id === 'string')) {
      throw new TypeError(`The type of 'id' must be String. Actual: ${typeof id}`)
    }
    const result = this.lessons[this.lessons.findIndex(l => l.hasId(id))]
    return (result) ? result : null
  }

  get() {
    return this.lessons
  }

  length() {
    return this.lessons.length
  }

  hasLessons () {
    return (this.length() > 0)
  }


  register(lesson) {
    if (!(lesson instanceof Lesson)) {
      throw new TypeError(`'lesson' must be an instance of [object Lesson]. Actual: ${typeof lesson}`)
    }
    this.lessons.push(lesson)
    return this
  }

  remove(lesson) {
    if (!(lesson instanceof Lesson)) {
      throw new TypeError(`'lesson' must be an instance of [object Lesson]. Actual: ${typeof lesson}`)
    }
    this.lessons.splice(
      this.lessons.findIndex(l => {
        return l.is(lesson)
      }), 1)
    return this
  }

  getStudentIds() {
    const studentIds = this.lessons.map(lesson => lesson.studentId)
    return Array.from(new Set(studentIds))
  }

  filterByStudentId(studentId) {
    const lessons = this.lessons.filter(lesson => lesson.studentId === studentId)
    return new Lessons(lessons)
  }

  toObjects() {
    return this.lessons.map(lesson => {
      return lesson.toObject()
    })
  }

}

export { Lesson, Lessons }